import type { PatientAction, PatientActionCheckIn, ScheduledMessage } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Button, Text } from "@ferrivia/ui";
import { Clock, Plus } from "lucide-react";
import { KIND_ICON, useActionLabels } from "../../../../actions/kinds";
import { activeActions, currentCheckIn, dueLabelKind } from "../../../../actions/plan";
import { messageText } from "../../../../components/ScheduleDialog/schedule";
import s from "./PlanStrip.module.css";

export interface PlanStripProps {
  actions: PatientAction[];
  checkIns: PatientActionCheckIn[];
  messages: ScheduledMessage[];
  clientName: string;
  onAdd: () => void;
  onOpenAction: (actionId: string) => void;
  onOpenScheduled: (message: ScheduledMessage) => void;
}

function ActionRow({
  action,
  checkIns,
  onOpen,
}: {
  action: PatientAction;
  checkIns: PatientActionCheckIn[];
  onOpen: (actionId: string) => void;
}) {
  const { t } = useTranslation("clinic");
  const { kindLabel, titleLabel, formatDay, formatTimestampDay } = useActionLabels();
  const Icon = KIND_ICON[action.kind];
  const due = currentCheckIn(action, checkIns).on;
  const dueLabel =
    dueLabelKind(action.kind) === "drawBefore"
      ? t("customerWorkspace.plan.drawBefore", "Draw before")
      : t("customerWorkspace.plan.checkIn", "Check-in");

  return (
    <li>
      <button type="button" className={`${s.row} ${s.action}`} onClick={() => onOpen(action.id)}>
        <span className={s.tile} aria-hidden="true" title={kindLabel(action.kind)}>
          <Icon size={18} />
        </span>
        <span className={s.body}>
          <span className={s.actionTitle}>{action.title}</span>
          <span className={s.meta}>
            {t("customerWorkspace.plan.actionAuthor", {
              name: action.authorName,
              title: titleLabel(action.authorTitle),
              date: formatTimestampDay(action.createdAt),
              defaultValue: "{{name}}, {{title}} · {{date}}",
            })}
          </span>
        </span>
        <span className={s.due}>
          {due ? (
            <>
              <span className={s.meta}>{dueLabel}</span>
              <span className={s.dueDate}>{formatDay(due)}</span>
            </>
          ) : (
            <span className={s.dueDate}>{t("customerWorkspace.plan.ongoing", "Ongoing")}</span>
          )}
        </span>
      </button>
    </li>
  );
}

function ScheduledRow({
  message,
  clientName,
  onOpen,
}: {
  message: ScheduledMessage;
  clientName: string;
  onOpen: (message: ScheduledMessage) => void;
}) {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();
  const held = message.state === "held";
  const sendAt = new Date(message.sendAt);
  const when = new Intl.DateTimeFormat(tag, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(sendAt);

  let replyBy: string | null = null;
  if (message.ifNoReply !== "none" && message.replyByDays !== null) {
    const d = new Date(sendAt);
    d.setDate(d.getDate() + message.replyByDays);
    replyBy = t("customerWorkspace.plan.replyBy", {
      date: new Intl.DateTimeFormat(tag, { weekday: "short", day: "numeric", month: "short" }).format(d),
      defaultValue: "reply by {{date}}",
    });
  }

  const author = message.authorName
    ? t("customerWorkspace.plan.byAuthor", { name: message.authorName, defaultValue: "by {{name}}" })
    : null;

  return (
    <li>
      <button
        type="button"
        className={held ? `${s.row} ${s.scheduled} ${s.held}` : `${s.row} ${s.scheduled}`}
        onClick={() => onOpen(message)}
      >
        <Clock aria-hidden size={18} className={s.icon} />
        <span className={s.body}>
          <span className={s.top}>
            <span className={s.kind}>{t("customerWorkspace.plan.kindMessage", "Scheduled message")}</span>
            <span className={s.when}>
              {held
                ? t("customerWorkspace.plan.onHold", { name: clientName, defaultValue: "On hold, {{name}} wrote" })
                : t("customerWorkspace.plan.sends", { when, defaultValue: "Sends {{when}}" })}
            </span>
          </span>
          <span className={s.snippet}>{messageText(message)}</span>
          {replyBy || author ? (
            <span className={s.foot}>
              {replyBy ? <span className={s.meta}>{replyBy}</span> : null}
              {author ? <span className={`${s.meta} ${s.author}`}>{author}</span> : null}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

export function PlanStrip({ actions, checkIns, messages, clientName, onAdd, onOpenAction, onOpenScheduled }: PlanStripProps) {
  const { t } = useTranslation("clinic");
  const active = activeActions(actions, checkIns);
  const empty = active.length === 0 && messages.length === 0;
  return (
    <section className={s.root} aria-label={t("customerWorkspace.plan.title", "Plan")}>
      <div className={s.header}>
        <Text as="h2" size="sm" weight="bold">
          {t("customerWorkspace.plan.title", "Plan")}
        </Text>
        {!empty ? <span className={s.count}>{active.length + messages.length}</span> : null}
        <Button variant="ghost" className={s.add} onClick={onAdd}>
          <Plus aria-hidden size={16} />
          {t("customerWorkspace.plan.add", "Add")}
        </Button>
      </div>
      {empty ? (
        <p className={s.empty}>
          {t("customerWorkspace.plan.empty", "No actions yet. Create one from a message, or add one here.")}
        </p>
      ) : (
        <ul className={s.list}>
          {active.map((action) => (
            <ActionRow key={action.id} action={action} checkIns={checkIns} onOpen={onOpenAction} />
          ))}
          {messages.map((message) => (
            <ScheduledRow key={message.id} message={message} clientName={clientName} onOpen={onOpenScheduled} />
          ))}
        </ul>
      )}
    </section>
  );
}
